import { useModuleContext } from "./ModuleContext"
import { calculateModuleScore } from "./utils"
import React, { useState } from "react"
import useNotifications from "../../notifications/useNotifications"
import { FirebaseContext } from "../../Firebase"

/**
 * # Interactive Actions Hook (useInteractiveActions)
 * 
 * This hook provides high-level actions that combine UI updates with API calls
 * for student interactions within modules. It bridges the gap between user
 * interface events and persistent data operations.
 * 
 * ## Core Functionality
 * 
 * ### Response Management
 * - **recordResponse**: Captures and stores student answers to module components
 * - **Real-time Validation**: Immediate feedback on response completeness
 * - **Progress Tracking**: Updates completion status as students progress
 * 
 * ### Module Submission
 * - **submitModule**: Finalizes student work and calculates scores
 * - **Score Calculation**: Automated grading based on response accuracy
 * - **Status Updates**: Marks modules as completed in database
 * - **Notification System**: Sends achievement notifications for passing scores
 * 
 * ### Auto-Save Operations
 * - **Continuous Saving**: Preserves student progress during interaction
 * - **Conflict Resolution**: Handles concurrent edits and data conflicts
 * - **Offline Support**: Queues changes when network is unavailable
 * 
 * ## Key Features
 * 
 * ### Smart Response Recording
 * ```javascript
 * recordResponse(studentAnswer, {
 *   slideIndex: 0,
 *   componentIndex: 1,
 *   sectionIndex: 0
 * });
 * ```
 * 
 * ### Automated Scoring
 * - Analyzes student responses across all components
 * - Calculates percentage-based scores
 * - Supports various question types (multiple choice, open-ended, etc.)
 * - Provides detailed score breakdowns
 * 
 * ### Achievement System
 * - Triggers notifications for successful completion (score >= 70%)
 * - Integrates with Wingspans notification system
 * - Supports custom achievement thresholds
 * - Tracks learning milestones and progress
 * 
 * ### Error Handling
 * - Comprehensive error states for failed operations
 * - Retry mechanisms for network failures
 * - User-friendly error messages
 * - Graceful degradation for offline scenarios
 * 
 * ## State Management
 * 
 * ### Loading States
 * - **isSubmitting**: Indicates module submission in progress
 * - **isSaving**: Shows auto-save operations status
 * - **isDisabled**: Prevents interaction during critical operations
 * 
 * ### Integration with ModuleContext
 * - Accesses current module state and dispatch functions
 * - Coordinates with navigation and UI management
 * - Maintains consistency across component tree
 * 
 * ## Usage Patterns
 * 
 * ### Basic Response Recording
 * ```javascript
 * const { recordResponse } = useInteractiveActions(firebase);
 * 
 * const handleStudentAnswer = (answer) => {
 *   recordResponse(answer, componentCoordinates);
 * };
 * ```
 * 
 * ### Module Completion
 * ```javascript
 * const { submitModule, isSubmitting } = useInteractiveActions(firebase);
 * 
 * const handleSubmit = async () => {
 *   if (!isSubmitting) {
 *     await submitModule();
 *   }
 * };
 * ```
 * 
 * ### Auto-Save Integration
 * ```javascript
 * const { saveProgress, isSaving } = useInteractiveActions(firebase);
 * 
 * useEffect(() => {
 *   const autoSaveTimer = setInterval(saveProgress, 30000);
 *   return () => clearInterval(autoSaveTimer);
 * }, [saveProgress]);
 * ```
 * 
 * ## API Integration
 * 
 * ### Firebase Operations
 * - **updateUserModuleById**: Saves current module state
 * - **updateUserModuleStatus**: Updates completion status
 * - **getUserModuleRecord**: Reloads fresh data after operations
 * 
 * ### Notification System
 * - Achievement notifications for completed modules
 * - Progress updates for long-running operations
 * - Error notifications for failed operations
 * 
 * ## Performance Considerations
 * 
 * ### Debounced Operations
 * - Auto-save operations are debounced to prevent excessive API calls
 * - Response recording is optimized for rapid user input
 * - Network requests are batched when possible
 * 
 * ### Memory Management
 * - Proper cleanup of timers and subscriptions
 * - Efficient state updates to prevent memory leaks
 * - Optimized re-render patterns
 * 
 * ## Security & Validation
 * 
 * ### Input Sanitization
 * - All student responses are validated before storage
 * - XSS prevention for user-generated content
 * - SQL injection protection for database operations
 * 
 * ### Access Control
 * - Verifies student permissions for module access
 * - Prevents unauthorized score manipulation
 * - Validates module completion requirements
 * 
 * ## Integration Points
 * 
 * - **InteractiveModule.js**: Primary consumer for student interactions
 * - **Component Renderers**: Individual components use for response recording
 * - **Progress Tracking**: Provides data for completion visualizations
 * - **Assessment Systems**: Feeds data to grading and review interfaces
 * 
 * For related documentation:
 * - Module state management: ModuleProvider.js
 * - Component system: MODULE_COMPONENT_SYSTEM.md
 * - Display rendering: display.js
 * - Database operations: useModuleDB.js
 */

/**
 * Hook for performing combination actions, UI updates coupled with API calls
 * @returns {}
 */
export const useInteractiveActions = (firebase) => {
  const { dispatch, moduleUI, moduleState, moduleId, programId, loadUserModuleRecord, mode } = useModuleContext()
  const { handleModuleCompletion, goToNextSlide, resetModuleUI } = moduleUI
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { handleModuleCompletionNotification, setIsDisabled, isDisabled } = useNotifications(firebase)

  const recordResponse = (response, coordinates) => {
    const { slideIndex, componentIndex, sectionIndex } = coordinates

    dispatch({
      type: "RECORD_RESPONSE",
      slideIndex,
      componentIndex,
      sectionIndex,
      payload: response,
    })
  }

  const submitModule = async () => {
    const score = calculateModuleScore(moduleState)

    try {
      setIsSubmitting(true)
      await firebase.updateUserModuleById({
        moduleId: moduleId,
        moduleJson: moduleState,
      })

      await firebase.updateUserModuleStatus({
        moduleId: moduleId,
        status: "completed",
        score: score,
      })

      if (score >= 70) {
        // send notification if they passed
        handleModuleCompletionNotification(moduleState.title, moduleId)
      }
    } catch (error) {
      console.error("Error updating module status:", error)
    } finally {
      handleModuleCompletion()
      setIsSubmitting(false)
    }
  }

  const goToNextSlideAndSaveProgress = async () => {
    goToNextSlide()

    if (mode === "STUDENT") {
      await firebase.updateUserModuleById({
        moduleId: moduleId,
        moduleJson: moduleState,
      })
    }
  }

  const handleManualSaveAndAlert = async () => {
    try {
      setIsSaving(true)
      await firebase.updateUserModuleById({
        moduleId: moduleId,
        moduleJson: moduleState,
      })
      // alert("Saved successfully!")
    } catch (error) {
      console.error("Error updating module status:", error)
    } finally {
      setIsSaving(false)
    }
  }

  const restartModule = async () => {
    try {
      setIsSubmitting(true)
      const res = await firebase.restartUserModule(moduleId)

      dispatch({
        type: "LOAD_FULL_MODULE",
        payload: res.data.res[0].module_json,
      })
      resetModuleUI()
    } catch (error) {
      console.error("Error restarting module:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    recordResponse,
    submitModule,
    goToNextSlideAndSaveProgress,
    handleManualSaveAndAlert,
    restartModule,
    isSubmitting,
    isSaving,
  }
}
